// https://codingjump.com/posts/2022/react-zoom
import React, { useState, FunctionComponent, Fragment, useRef } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { Loader, LoaderType } from '../Loader'
import classnames from 'classnames'

interface IDefaultProps {
  border: boolean
  src: string
  thumbnailSrc?: string
  className: string
  containerClassName?: string
  blockClick?: boolean
  style?: React.CSSProperties
}

interface IProps extends Partial<IDefaultProps> {}

const defaultProps: IDefaultProps = {
  className: null,
  border: false,
  src: null,
  thumbnailSrc: null,
  containerClassName: null,
  blockClick: false,
  style: {},
}

const ImageZoom: FunctionComponent<IProps> = ({
  src,
  thumbnailSrc,
  border,
  className,
  containerClassName,
  blockClick,
  style,
  ...rest
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [click, setClick] = useState(false)
  const [open, setOpen] = useState(true)
  const cancelButtonRef = useRef(null)

  const closeModal = () => {
    setOpen(false)
  }

  const handleImageLoaded = () => {
    setIsLoading(false)
  }

  return (
    <>
      <div className={classnames('relative', containerClassName)}>
        <img
          {...rest}
          src={thumbnailSrc ?? src}
          alt=''
          className={className}
          onClick={() => {
            // TIFF preview does not work on Chrome & Firefox
            if (src.endsWith('.tiff')) {
              return
            }

            if (blockClick) {
              return
            }

            setClick(true)
            setOpen(true)
          }}
          style={style}
          onLoad={handleImageLoaded}
        />
        {isLoading ? (
          <div className='rounded-xl flex items-center justify-center absolute w-full h-full bg-gray-200 top-[0]'>
            <Loader type={LoaderType.button} />
          </div>
        ) : null}
      </div>
      {click && (
        <Transition.Root show={open} as={Fragment}>
          <Dialog
            as='div'
            className='relative z-50'
            onClose={closeModal}
            onClick={() => {
              setOpen(false)
              setClick(false)
            }}
          >
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
            </Transition.Child>
            <div className='fixed z-10 inset-0 overflow-y-auto cursor-pointer'>
              <div className='flex items-center justify-center min-h-full p-4 text-center sm:p-0'>
                <Transition.Child
                  as={Fragment}
                  enter='ease-out duration-300'
                  enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                  enterTo='opacity-100 translate-y-0 sm:scale-100'
                  leave='ease-in duration-200'
                  leaveFrom='opacity-100 translate-y-0 sm:scale-100'
                  leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                >
                  <img
                    {...rest}
                    src={src}
                    alt=''
                    className='relative max-h-screen max-w-screen p-2'
                    ref={cancelButtonRef}
                  />
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      )}
    </>
  )
}

ImageZoom.defaultProps = defaultProps

export { ImageZoom }
